import { useState, useEffect, useCallback } from 'react';
import { rpc, RpcError } from '@/api/client';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';

import { SessionList } from './SessionList';
import { SearchBar } from './SearchBar';
import { Pagination } from '@/components/common/Pagination';
import { Activity } from 'lucide-react';
import type { StatsResponse, ListSessionsResponse } from '@/types/telemetry';

export function Dashboard() {
  const { params, navigate } = useRouter();
  const { t } = useI18n();
  const page = parseInt(params.page, 10) || 1;
  const query = params.q || '';

  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [sessionsData, setSessionsData] = useState<ListSessionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const loadData = useCallback(async (currentPage: number, currentQuery: string) => {
    setLoading(true);
    setError(null);
    try {
      const rpcBody: Record<string, unknown> = { page: currentPage, pageSize: 10 };
      if (currentQuery) rpcBody.query = currentQuery;
      const statsBody: Record<string, unknown> = currentQuery ? { query: currentQuery } : {};

      const [statsRes, sessionsRes] = await Promise.all([
        rpc<StatsResponse>('GetStats', statsBody),
        rpc<ListSessionsResponse>('ListSessionsGrouped', rpcBody),
      ]);
      setStats(statsRes);
      setSessionsData(sessionsRes);
    } catch (e: any) {
      const nf = e instanceof RpcError && e.isNotFound;
      setNotFound(nf);
      setError(nf ? 'The requested resource could not be found.' : (e.message || t('dashboard.failedToLoadDashboard')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadData(page, query);
    const onQueueCompleted = () => {
      loadData(page, query);
    };
    window.addEventListener('queue:completed', onQueueCompleted);
    return () => window.removeEventListener('queue:completed', onQueueCompleted);
  }, [page, query, loadData]);

  const handleSearch = useCallback((newQuery: string) => {
    const qp = new URLSearchParams();
    qp.set('page', '1');
    if (newQuery) qp.set('q', newQuery);
    navigate(`#/?${qp.toString()}`);
  }, [navigate]);

  const handlePageChange = useCallback((newPage: number) => {
    const qp = new URLSearchParams();
    qp.set('page', String(newPage));
    if (query) qp.set('q', query);
    navigate(`#/?${qp.toString()}`);
  }, [query, navigate]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;
  if (!stats || !sessionsData) return <ErrorState message={t('dashboard.noDataAvailable')} />;

  const totalPages = Math.max(1, Math.ceil((sessionsData.totalCount || 0) / (sessionsData.pageSize || 10)));
  const currentPage = sessionsData.page || 1;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div className="w-9 h-9 rounded-lg bg-accent/10 border border-accent/20 flex items-center justify-center">
          <Activity size={18} className="text-accent" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('dashboard.overview')}</h1>
          <p className="text-xs text-text-muted mt-0.5">{t('dashboard.subtitle')}</p>
        </div>
      </div>

      {/* Search */}
      <div className="mt-6">
        <SearchBar initialQuery={query} onSearch={handleSearch} />
      </div>


      {/* Sessions */}
      <div className="mt-10">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="section-title">{t('dashboard.recentSessions')}</h2>
          <span className="text-xs text-text-muted">{t('dashboard.total', { n: sessionsData.totalCount || 0 })}</span>
        </div>
        <SessionList sessions={sessionsData.sessions || []} trackBests={stats.trackBests || []} resetKey={`${currentPage}-${query}`} />
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={sessionsData.totalCount}
        />
      </div>
    </div>
  );
}
